<script setup lang="ts">
import wait from '@/modules/common/services/wait';
import { errorString } from '@/utils/helpers/rest-response';
import { computed, onMounted, shallowRef } from 'vue';
import { store as vuex } from '@/store/store';
import { useRules } from '@/composables/vuetify-validation';

const emit = defineEmits<{ (event: 'close-modal'): void }>();

const cmpOTPInput = shallowRef<null | { focus(): void }>(null);

const loginState = computed(() => vuex.state.loginState!);
const has2faEnabled = computed(() => !!loginState.value.tfa?.tfaIsEnabled);

const otp = shallowRef('');
const countProcessing = shallowRef(0);
const showSuccess = shallowRef(false);

function closeDialog(): void {
  emit('close-modal');
}

function fetchCurrent2FA() {
  return vuex.dispatch('fetchCurrent2FA');
}

function focusOtpInput(): void {
  cmpOTPInput.value?.focus();
}

onMounted(() => {
  focusOtpInput();
  void fetchCurrent2FA();
});

const { rules, validate, isSubmitted } = useRules<{ otp: string }>({
  otp: {
    rules: [
      (value) =>
        !isSubmitted.value ||
        (typeof value === 'string' ? !!value.trim() : !!value) ||
        'Please enter a code to continue.',
      (value) => !isSubmitted.value || value?.length === 6 || 'Please enter a 6 digit code.',
      (value) => {
        if (value?.length !== 6) return true;
        // we always want to eagerly run the validation, once we have all the digits
        ++countProcessing.value;
        return vuex
          .dispatch('disable2FALogin', value)
          .then(async () => {
            // success, alert user and then close form
            showSuccess.value = true;

            // reload 2fa status in the background
            void fetchCurrent2FA();

            await wait(800);
            closeDialog();

            return true as const;
          })
          .catch((err) => {
            focusOtpInput();
            return errorString(err);
          })
          .finally(() => --countProcessing.value);
      },
    ],
  },
});

defineExpose({
  test: {
    setOTP: (v: string) => (otp.value = v),
  },
});
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="700"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
    @click:outside="closeDialog()"
    @keydown.esc="closeDialog()"
    @shortkey.native="closeDialog()"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          {{ $t('disable2FA.button') }}
        </VCardTitle>

        <VCardText v-if="!has2faEnabled && !countProcessing">
          Two-Factor Authentication is already disabled.
        </VCardText>
        <VCardText v-else>
          You are about to disable Two-Factor Authentication.
          <br />
          Disabling this feature will put your account at risk.
          <br />
          <VRow>
            <VCol class="pt-8">
              <p>Enter the 6-digit code from your app to confirm:</p>
              <VOtpInput
                ref="otpInput"
                v-model="otp"
                :disabled="!!countProcessing"
                v-bind="rules.otp"
                length="6"
                type="number"
              />
              <label class="error--text">{{ rules.otp.errorMessages.join('\n') }}</label>
            </VCol>
          </VRow>

          <VRow v-if="showSuccess">
            <VCol class="pa-0 px-1 col-6 offset-3">
              <div class="v-alert v-alert--dense text--primary text-body-2 text-center success">
                Two-Factor Authentication has been disabled.
              </div>
            </VCol>
          </VRow>
        </VCardText>

        <VCardActions>
          <VBtn color="secondary" data-test="cancel-btn" @click="closeDialog">
            {{ $tc('dialogs.cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            class="px-6"
            color="red"
            :disabled="!!countProcessing || !has2faEnabled"
            type="submit"
            @click="validate()"
          >
            Disable 2FA
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>
